<script setup>
import { ref, computed, onMounted } from 'vue'
import ProductCard from './ProductCard.vue'

const props = defineProps({
  product: { type: Object, required: true }
})

const accessories = ref([])
const loading = ref(true)
const showAll = ref(false)

const displayedAccessories = computed(() => {
  return showAll.value ? accessories.value : accessories.value.slice(0, 4)
})

onMounted(async () => {
  try {
    let relatedIds = []
    if (props.product.related_products) {
      try {
        relatedIds = typeof props.product.related_products === 'string' 
          ? JSON.parse(props.product.related_products) 
          : props.product.related_products
      } catch(e) {}
    }
    if (!Array.isArray(relatedIds)) relatedIds = []

    if (relatedIds.length === 0) {
      loading.value = false
      return
    }

    const res = await fetch('/api/products')
    const data = await res.json()
    if (data.success) {
      accessories.value = data.data.filter(p => {
        return relatedIds.some(id => String(id) === String(p.id))
      }).map(p => ({
        ...p,
        title: p.title || p.name,
        image: p.image_url || p.image || 'https://via.placeholder.com/400x300?text=No+Image',
      }))
    }
  } catch (error) {
    console.error('Failed to load accessories:', error)
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div v-if="!loading && accessories.length > 0" class="mt-14 mb-10">
    <div class="bg-white dark:bg-[#111827] border border-gray-100 dark:border-gray-800 rounded-[1.5rem] shadow-sm p-5 md:p-6 relative overflow-hidden">
      
      <!-- Subtle Background Accent -->
      <div class="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 dark:bg-emerald-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none z-0"></div>

      <!-- Compact Header -->
      <div class="flex items-center justify-between mb-5 relative z-10">
        <div class="flex items-center gap-3 md:gap-4">
          <div>
            <h2 class="text-lg md:text-xl font-bold text-gray-900 dark:text-white tracking-tight leading-none mb-1">อะไหล่และอุปกรณ์เสริม</h2>
            <p class="text-[11px] md:text-xs text-gray-500 dark:text-gray-400 font-medium">ยกระดับการใช้งานให้สมบูรณ์แบบยิ่งขึ้นด้วยอุปกรณ์เสริมแท้</p>
          </div>
        </div>
        
        <button 
          v-if="accessories.length > 4" 
          @click="showAll = !showAll"
          class="shrink-0 text-[11px] md:text-xs font-bold text-emerald-800 hover:text-emerald-900 bg-emerald-100/60 hover:bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 px-3 py-1.5 md:px-4 md:py-2 rounded-lg transition-colors flex items-center gap-1.5"
        >
          {{ showAll ? 'แสดงน้อยลง' : 'ดูทั้งหมด' }}
          <span class="bg-white/80 dark:bg-black/40 text-emerald-900 dark:text-emerald-200 px-1.5 py-0.5 rounded text-[9px]">{{ accessories.length }}</span>
        </button>
      </div>
      
      <!-- Standard Product Cards Grid (Respects Admin System Settings) -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-6 relative z-10">
        <ProductCard 
          v-for="item in displayedAccessories" 
          :key="item.id" 
          :product="item"
        />
      </div>

    </div>
  </div>
</template>
